/**
 * SourceBadge - Badge avec logo et nom de source
 * Affiche logo + nom + lien vers site officiel
 * Utilisé dans: recherche, cartes résultats, fiche substance
 */

import { SOURCES_CONFIG, SourceKey } from '../../config/sources'

interface SourceBadgeProps {
  sourceKey: SourceKey | string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showName?: boolean
  showLink?: boolean
  variant?: 'default' | 'compact' | 'full'
  className?: string
}

const SIZE_CLASSES = {
  sm: {
    logo: 'h-4 w-auto',
    text: 'text-xs',
    gap: 'gap-1',
    padding: 'px-1.5 py-0.5',
  },
  md: {
    logo: 'h-5 w-auto',
    text: 'text-sm',
    gap: 'gap-1.5',
    padding: 'px-2 py-1',
  },
  lg: {
    logo: 'h-6 w-auto',
    text: 'text-base',
    gap: 'gap-2',
    padding: 'px-3 py-1.5',
  },
  xl: {
    logo: 'h-8 w-auto',
    text: 'text-lg',
    gap: 'gap-2.5',
    padding: 'px-4 py-2',
  },
}

export default function SourceBadge({
  sourceKey,
  size = 'md',
  showName = true,
  showLink = true,
  variant = 'default',
  className = '',
}: SourceBadgeProps) {
  const config = SOURCES_CONFIG[sourceKey as SourceKey]

  // Si source non reconnue, afficher un badge générique
  if (!config) {
    return (
      <span className={`inline-flex items-center ${SIZE_CLASSES[size].gap} ${SIZE_CLASSES[size].padding} bg-gray-100 rounded ${className}`}>
        <span className={`${SIZE_CLASSES[size].text} font-medium text-gray-600`}>
          {sourceKey}
        </span>
      </span>
    )
  }

  const content = (
    <>
      <img
        src={config.logo}
        alt={`Logo ${config.name}`}
        className={`${SIZE_CLASSES[size].logo} object-contain`}
      />
      {showName && (
        <span className={`${SIZE_CLASSES[size].text} font-medium`}>
          {config.name}
        </span>
      )}
    </>
  )

  // Variante compacte: juste le logo
  if (variant === 'compact') {
    if (showLink) {
      return (
        <a
          href={config.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Ouvrir le site ${config.name} (nouvel onglet)`}
          className={`inline-block hover:opacity-70 hover:scale-105 transition-all cursor-pointer ${className}`}
          title={`Consulter ${config.fullName}`}
        >
          <img
            src={config.logo}
            alt={`Logo ${config.name}`}
            className={`${SIZE_CLASSES[size].logo} object-contain`}
          />
        </a>
      )
    }
    return (
      <img
        src={config.logo}
        alt={`Logo ${config.name}`}
        className={`${SIZE_CLASSES[size].logo} object-contain ${className}`}
      />
    )
  }

  // Variante full: avec fond coloré
  if (variant === 'full') {
    const badgeClasses = `inline-flex items-center ${SIZE_CLASSES[size].gap} ${SIZE_CLASSES[size].padding} rounded-lg bg-white border-2 shadow-sm`

    if (showLink) {
      return (
        <a
          href={config.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${badgeClasses} ${config.borderColor} hover:shadow-md transition-shadow ${className}`}
          title={`Consulter ${config.fullName}`}
        >
          {content}
          <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      )
    }
    return (
      <span className={`${badgeClasses} ${config.borderColor} ${className}`}>
        {content}
      </span>
    )
  }

  // Variante default
  if (showLink) {
    return (
      <a
        href={config.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center ${SIZE_CLASSES[size].gap} hover:opacity-80 transition-opacity ${className}`}
        title={`Consulter ${config.fullName}`}
      >
        {content}
      </a>
    )
  }

  return (
    <span className={`inline-flex items-center ${SIZE_CLASSES[size].gap} ${className}`}>
      {content}
    </span>
  )
}

/**
 * SourceLink - Bouton lien vers source officielle
 * Pour le SourceDetailPanel footer
 */
interface SourceLinkProps {
  sourceKey: SourceKey | string
  substanceUrl?: string // URL spécifique à la substance si disponible
  className?: string
}

export function SourceLink({ sourceKey, substanceUrl, className = '' }: SourceLinkProps) {
  const config = SOURCES_CONFIG[sourceKey as SourceKey]

  if (!config) {
    return null
  }

  // Utiliser l'URL spécifique si fournie, sinon l'URL générale
  const url = substanceUrl || config.url

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`
        flex items-center justify-center gap-3 w-full
        px-4 py-3 rounded-lg font-medium
        bg-white border-2 ${config.borderColor}
        ${config.textColor} hover:bg-gray-50
        transition-colors ${className}
      `}
    >
      <img
        src={config.logo}
        alt={`Logo ${config.name}`}
        className="h-6 w-auto object-contain"
      />
      <span>Consulter {config.name}</span>
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  )
}
